import { useCallback, useMemo, useState, type FunctionComponent } from "react";
import { Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  ChallengeEventState,
  getChallengeEventState,
  type ChallengeEventEntity,
} from "@fitness-app/data-models/entities/Challenge";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { CalendarChip, CalendarChipPreview } from "~/components/EventCalendar/CalendarChip";
import { CalendarDndContext } from "~/components/EventCalendar/CalendarDndContext";
import {
  getViewTitle,
  groupByDay,
  moveToDay,
  shiftViewDate,
  toDayKey,
  type CalendarView,
  type DayMenuFactory,
  type RenderEventOptions,
} from "~/components/EventCalendar/calendarModel";
import { CalendarToolbar } from "~/components/EventCalendar/CalendarToolbar";
import { CalendarViewSwitch } from "~/components/EventCalendar/CalendarViewSwitch";

/** Hour a day click defaults to — a whole-day click carries no time of its own. */
const DEFAULT_EVENT_HOUR = 10;

const VIEWS: CalendarView[] = ["month", "week", "agenda"];

const atDefaultHour = (day: Dayjs) => day.hour(DEFAULT_EVENT_HOUR).minute(0).second(0).millisecond(0);

// Same meaning as the state tags in the table view: live red, upcoming green, past grey.
const STATE_TONES: Record<ChallengeEventState, string> = {
  [ChallengeEventState.Live]: "border-red-200 bg-red-50 text-red-900 hover:border-red-300 hover:bg-red-100",
  [ChallengeEventState.Upcoming]:
    "border-emerald-100 bg-emerald-50 text-emerald-950 hover:border-emerald-200 hover:bg-emerald-100",
  [ChallengeEventState.Past]: "border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100",
};

type Props = {
  events: ChallengeEventEntity[];
  challengeStartDate: string | null | undefined;
  challengeEndDate: string | null | undefined;
  loading: boolean;
  /** Day picked on the grid — opens the form prefilled instead of at the challenge default. */
  onCreateAt: (startAt: Dayjs, durationMinutes: number | null, repeat?: "once" | "weekly") => void;
  onEdit: (event: ChallengeEventEntity) => void;
  /** Dragging one occurrence to another day; the series (if any) stays put. */
  onMove: (event: ChallengeEventEntity, startAt: Dayjs) => void;
};

const getEventTone = (event: ChallengeEventEntity) => STATE_TONES[getChallengeEventState(event)];

const getEventTimeRange = (event: ChallengeEventEntity) =>
  event.endAt
    ? `${dayjs(event.startAt).format("HH:mm")} - ${dayjs(event.endAt).format("HH:mm")}`
    : dayjs(event.startAt).format("HH:mm");

const EventChipContent = ({ event }: { event: ChallengeEventEntity }) => (
  <div className="gap-1.5">
    <span className="shrink-0 tabular-nums opacity-70">{dayjs(event.startAt).format("HH:mm")}</span>
    <span className="min-w-0 flex-1 truncate">{event.title}</span>
  </div>
);

/**
 * TRE-1654 — the same `challenge_event` rows as the table, on a month/week grid. Clicking empty space
 * in a day opens the form on that day, so a trainer building a schedule doesn't have to retype dates;
 * dragging an event moves that single occurrence to another day at the same hour.
 */
const ChallengeEventsCalendar: FunctionComponent<Props> = ({
  events,
  challengeStartDate,
  challengeEndDate,
  loading,
  onCreateAt,
  onEdit,
  onMove,
}) => {
  // Open on the challenge window rather than "today" — an upcoming edition is usually weeks away.
  const [currentDate, setCurrentDate] = useState<Date>(() => {
    const start = challengeStartDate ? dayjs(challengeStartDate) : null;
    return start?.isAfter(dayjs()) ? start.toDate() : new Date();
  });
  const [view, setView] = useState<CalendarView>("month");

  const eventsByDay = useMemo(() => {
    const byDay = groupByDay(events, (event) => event.startAt);
    for (const dayEvents of byDay.values()) {
      dayEvents.sort((a, b) => a.startAt.localeCompare(b.startAt));
    }
    return byDay;
  }, [events]);

  const dayMenu = useCallback<DayMenuFactory>(
    (day) => [
      {
        key: "single",
        label: `Dodaj wydarzenie (${day.format("DD.MM")})`,
        onClick: () => onCreateAt(atDefaultHour(day), null),
      },
      {
        key: "weekly",
        label: `Dodaj serię co ${day.locale("pl").format("dddd")}`,
        onClick: () => onCreateAt(atDefaultHour(day), null, "weekly"),
      },
    ],
    [onCreateAt],
  );

  // Days outside the challenge window are dimmed — events there still work, they're just off-plan.
  const isDayDimmed = useCallback(
    (day: Dayjs) =>
      Boolean(
        challengeStartDate &&
        challengeEndDate &&
        (day.isBefore(dayjs(challengeStartDate), "day") || day.isAfter(dayjs(challengeEndDate), "day")),
      ),
    [challengeStartDate, challengeEndDate],
  );

  const renderEvent = useCallback(
    (event: ChallengeEventEntity, { draggable, dragIdScope }: RenderEventOptions) => (
      <CalendarChip
        dragId={dragIdScope ? `${dragIdScope}:${event.id}` : event.id}
        dragData={{ event }}
        canDrag={draggable}
        toneClassName={getEventTone(event)}
        title={`${getEventTimeRange(event)} ${event.title}`}
        onClick={() => onEdit(event)}
      >
        <EventChipContent event={event} />
      </CalendarChip>
    ),
    [onEdit],
  );

  const onDrop = useCallback(
    (data: Record<string, unknown>, dayKey: string) => {
      const event = data.event as ChallengeEventEntity | undefined;
      if (event && toDayKey(event.startAt) !== dayKey) {
        onMove(event, moveToDay(event.startAt, dayKey));
      }
    },
    [onMove],
  );

  const viewDate = dayjs(currentDate);

  return (
    <div className="flex flex-col gap-3">
      <Typography.Text type="secondary">
        Kliknij dzień, aby dodać wydarzenie. Prawy przycisk albo „+” przy dacie pozwala dodać też serię co tydzień.
        Przeciągnij wydarzenie, aby przenieść pojedynczy termin.
      </Typography.Text>
      <CalendarToolbar
        title={getViewTitle(view, viewDate)}
        view={view}
        views={VIEWS}
        onViewChange={setView}
        onPrev={() => setCurrentDate((prev) => shiftViewDate(view, prev, -1))}
        onNext={() => setCurrentDate((prev) => shiftViewDate(view, prev, 1))}
        onToday={() => setCurrentDate(new Date())}
      />
      <div className={cn("transition-opacity", loading && "opacity-60")}>
        <CalendarDndContext
          onDrop={onDrop}
          renderOverlay={(data) => {
            const event = data.event as ChallengeEventEntity;
            return (
              <CalendarChipPreview toneClassName={getEventTone(event)}>
                <EventChipContent event={event} />
              </CalendarChipPreview>
            );
          }}
        >
          <CalendarViewSwitch
            view={view}
            date={viewDate}
            eventsByDay={eventsByDay}
            getEventKey={(event) => event.id}
            renderEvent={renderEvent}
            dayMenu={dayMenu}
            onDayClick={(day) => onCreateAt(atDefaultHour(day), null)}
            isDayDimmed={isDayDimmed}
          />
        </CalendarDndContext>
      </div>
    </div>
  );
};

export default ChallengeEventsCalendar;
